import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { LoadingComponent } from "src/app/inspection/dialogs/loading/loading.component";
import { Item } from "src/app/inspection/model/item";
import { ToggleSectionsForForm } from "src/app/inspection/new-inspection/models/toggle-section";
import { ProductsListConfig } from "src/app/inspection/permissions-setter/permissions-interface";

import { AddItemsToInspectionCommand } from "../../services/commands/add-items-to-inspection.command";
import { ItemsService } from "../../services/items.service";
import { ItemsLightQuery } from "../../services/queries/items.light.query";

@Component({
  selector: "im-items-creation",
  templateUrl: "./items-creation.component.html",
  styleUrls: ["./items-creation.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ItemsCreationComponent {
  @Input()
  inspectionId: string = null;

  @Input()
  itemId: string = null;

  @Input()
  config: ProductsListConfig = {} as ProductsListConfig;

  @Input()
  defaultFormId: string;

  @Input()
  allowToDisableSections: boolean;

  @Input()
  items: Item[] = [];

  @Input()
  toggleSectionsForForm: ToggleSectionsForForm[] = [];

  @Output()
  toggleChanged: EventEmitter<{ formId: string; sectionId: string }> =
    new EventEmitter<{ formId: string; sectionId: string }>();

  @Output()
  itemDeleted: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  itemsAdded: EventEmitter<Item[]> = new EventEmitter<Item[]>();

  @Output()
  itemEdited: EventEmitter<Item> = new EventEmitter<Item>();

  isLoading: boolean = false;
  showSpeedDial: boolean = true;
  sectionsExpanded: boolean = true;

  constructor(
    private dialog: MatDialog,
    private itemsService: ItemsService,
    private snackBarModule: MatSnackBar,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  public displayManageSections(): boolean {
    return !!this.config?.allowManageSections && this.items?.length > 0;
  }

  public async onDeletedItem(itemToDelete: Item): Promise<void> {
    this.itemDeleted.next(itemToDelete.id);
  }

  public async onEditedItem(itemToEdit: Item): Promise<void> {
    this.itemEdited.next(itemToEdit);
  }

  async onItemsCreated(items: Item[]) {
    if (!this.creationMode()) {
      const command = new AddItemsToInspectionCommand(items, this.inspectionId);
      const query = new ItemsLightQuery(this.inspectionId);
      const loadingDialog = this.openLoadingDialog(
        `Inserting ${items?.length > 1 ? "items" : "item"}...`,
      );
      try {
        await this.itemsService.invokeCommand(command);
        await this.itemsService.invokeQuery(query);
      } catch {
        this.snackBarModule.open(
          "Insertion failed. Try again later.",
          "DISMISS",
        );
      } finally {
        loadingDialog.close();
        this.changeDetectorRef.detectChanges();
      }
    } else {
      this.itemsAdded.next(items);
    }
  }

  public async onToggleChanged(formId: string, sectionId: string) {
    this.toggleChanged.next({ formId, sectionId });
  }

  public onItemSectionsDestroyed(): void {
    //modifying the height for the items-container component (full height)
    const itemsContainer = document.querySelector(
      "im-items-container",
    ) as HTMLElement;
    if (itemsContainer)
      itemsContainer.style.height = `calc(100vh - (266px + 20px))`;
  }

  public onSelectedItemChanged(selectedItem: Item): void {
    this.showSpeedDial = selectedItem ? false : true;
    this.sectionsExpanded = selectedItem ? false : true;
  }

  public creationMode(): boolean {
    return this.inspectionId === null;
  }

  private openLoadingDialog(textToDisplay: string): MatDialogRef<any> {
    return this.dialog.open(LoadingComponent, {
      data: <LoadingComponent>{
        loadingText: textToDisplay,
      },
      panelClass: "dialog",
    });
  }
}
